import { useState, useEffect, useMemo } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { CompanyType } from "../types/company";
import getCompanyListApi, { CompanyQuery } from "../api/companyApi";

/**
 * Custom hook to fetch and manage a paginated list of companies.
 * This hook uses a placeholder for the actual API call. Replace the commented-out fetchClients function with the actual API call to fetch companies.
 */
const useYhCompanyList = () => {
  const [queryParams, setQueryParams] = useState<{
    companyName: string;
    deleteFlag: boolean;
  }>({
    companyName: "",
    deleteFlag: true,
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState<CompanyType[]>([]);

  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);

  /**
   * @param body CompanyQuery
   */
  const getCompanies = async (body?: CompanyQuery) => {
    try {
      const data = await getCompanyListApi(body);
      if (body && body?.offset > 1) {
        setCompanies((prev) => [...prev, ...data.list]);
      } else {
        setCompanies(data.list);
      }
      setTotal(data.total);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  useEffect(() => {
    getCompanies({ companyName: "", deleteFlag: true, offset: 1 });
  }, []);

  const handleSearch = () => {
    getCompanies({
      ...queryParams,
      offset: 1,
    });
  };

  const onLoadMore = async () => {
    if (page + 1 > totalPage) return;
    setLoading(true);
    setPage((prevPage) => prevPage + 1);
    await getCompanies({ ...queryParams, offset: page + 1 });
  };

  return {
    companies,
    queryParams,
    total,
    totalPage,
    page,
    loading,
    handleSearch,
    onLoadMore,
    setQueryParams,
  };
};

export default useYhCompanyList;
